import { PanelShell, Badge, DJSEMApiClient, useApiResource, CUE_SAFE_LANGUAGE } from "../index";

/**
 * Cue Director module — shows whether a verified Hugging Face cue-suggestion model is
 * configured and enabled, and explains that suggestions are always suggest-only, requiring
 * operator review before export (no real hardware output).
 *
 * @param client - The API client used to fetch the available cue models.
 * @returns A panel showing cue-model readiness and guidance on how to use cue suggestions.
 */
export function CueDirectorModule({ client }: { client: DJSEMApiClient }) {
  const models = useApiResource(() => client.listCueModels(), []);
  const modelItems = (models.view.state === "ready" || models.view.state === "empty" ? models.view.data?.items ?? [] : []) as { providerId: string; label: string; productionEnabled: boolean }[];
  const anyEnabled = modelItems.some((m) => m.productionEnabled);
  return (
    <PanelShell title={CUE_SAFE_LANGUAGE.heading}>
      <div style={{ fontSize: 11, color: "#8b95a5", marginBottom: 8 }}>{CUE_SAFE_LANGUAGE.disclaimer}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12 }}>Cue model</span>
        {models.view.state === "loading" ? <Badge tone="neutral">loading…</Badge>
          : models.view.state === "offline" ? <Badge tone="warn">offline</Badge>
          : anyEnabled ? <Badge tone="ok">ready</Badge> : <Badge tone="warn">disabled</Badge>}
      </div>
      {!anyEnabled ? (
        <div style={{ fontSize: 11, color: "#e8b24a" }}>
          No verified Hugging Face cue model is enabled. Configure and validate a model in Settings → AI Providers to generate suggestions. Suggestions remain suggest-only and require operator review before export.
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "#8b95a5" }}>Select a track or set, choose cue types, then run a suggestion job. Suggestions appear on a timeline for review; approved cues export to an immutable snapshot. Real hardware output is blocked.</div>
      )}
    </PanelShell>
  );
}
